import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const QuestionList = ({ criteria, onEditQuestion, onDeleteQuestion, onReorder, editingQuestionId }) => {
  const { t } = useTranslation('reviews');
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const next = [...criteria];
    [next[index], next[index - 1]] = [next[index - 1], next[index]];
    onReorder(next);
  };

  const handleMoveDown = (index) => {
    if (index === criteria?.length - 1) return;
    const next = [...criteria];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onReorder(next);
  };

  const typeLabel = (type) => t(`schemaBuilder.questionList.types.${type}`, type);
  const typeIcon = (type) => ({ scale: 'BarChart3', true_false: 'CheckSquare', short_text: 'Type', long_text: 'FileText' })[type] || 'HelpCircle';

  return (
    <div className="bg-card rounded-lg border border-border flex flex-col sticky top-6">
      <div className="px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">{t('schemaBuilder.questionList.heading')}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          {t('schemaBuilder.questionList.count', { count: criteria?.length || 0 })}
        </p>
      </div>

      {criteria?.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <Icon name="List" size={32} className="text-muted-foreground mb-3 opacity-40" />
          <p className="text-sm text-muted-foreground">{t('schemaBuilder.questionList.empty')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('schemaBuilder.questionList.emptyHint')}</p>
        </div>
      ) : (
        <div className="divide-y divide-border overflow-y-auto max-h-[calc(100vh-280px)]">
          {criteria?.map((question, index) => (
            <div
              key={question?.id}
              className={`p-3 transition-colors ${
                editingQuestionId === question?.id
                  ? 'bg-primary/5 border-l-2 border-l-primary'
                  : 'hover:bg-muted/30'
              }`}
            >
              <div className="flex items-start gap-2">
                {/* Reorder */}
                <div className="flex flex-col mt-0.5 flex-shrink-0">
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-25 disabled:cursor-not-allowed"
                  >
                    <Icon name="ChevronUp" size={14} />
                  </button>
                  <button
                    onClick={() => handleMoveDown(index)}
                    disabled={index === criteria?.length - 1}
                    className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-25 disabled:cursor-not-allowed"
                  >
                    <Icon name="ChevronDown" size={14} />
                  </button>
                </div>

                {/* Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  <Icon name={typeIcon(question?.questionType)} size={14} className="text-primary" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5">
                      {typeLabel(question?.questionType)}
                    </span>
                    {question?.isRequired && (
                      <span className="text-[10px] text-error">*</span>
                    )}
                    <span className="text-[10px] text-muted-foreground ml-auto">#{index + 1}</span>
                  </div>
                  <p className="text-sm text-foreground font-medium leading-snug line-clamp-2">
                    {question?.questionText}
                  </p>
                  {question?.questionType === 'scale' && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {question?.scaleMin}–{question?.scaleMax}
                      {question?.scaleMinLabel && question?.scaleMaxLabel && (
                        <span className="ml-1">({question?.scaleMinLabel} → {question?.scaleMaxLabel})</span>
                      )}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={() => onEditQuestion(question)}
                    className="p-1 text-muted-foreground hover:text-primary transition-colors"
                    title={t('schemaBuilder.questionList.edit')}
                  >
                    <Icon name="Edit" size={13} />
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(t('schemaBuilder.questionList.confirmDelete'))) onDeleteQuestion(question?.id);
                    }}
                    className="p-1 text-muted-foreground hover:text-error transition-colors"
                    title={t('schemaBuilder.questionList.delete')}
                  >
                    <Icon name="Trash2" size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {criteria?.length > 0 && (
        <div className="px-3 py-2 border-t border-border text-xs text-muted-foreground text-center">
          {t('schemaBuilder.questionList.countTotal', { count: criteria?.length || 0 })}
        </div>
      )}
    </div>
  );
};

export default QuestionList;
